import React, { memo } from "react";
import { Text, View, TouchableOpacity } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { LineChart } from "react-native-gifted-charts";
import Icons from "@/assets/icons";
import { router } from "expo-router";
import { UserFinanceDataResponse } from "@/services/userFinanceDataServices";
import { SubscriptionSetupType } from "@/app/my-money/setup";
import { Colors } from "@/constants/Colors";
import { SCREEN_WIDTH } from "@/constants/Sizes";

const CARD_MARGIN = 16; // 16px margin on each side
const CARD_PADDING = 24; // 24px padding on each side of card
const POINTER_LABEL_WIDTH = 100; // Width for the pointer label
const CHART_WIDTH = SCREEN_WIDTH - CARD_MARGIN - CARD_PADDING - 32; // Extra buffer for pointer label
const MAX_SPACING = 30; // Maximum pixels between chart points before centering

interface NetWorthChartProps {
  financeData: UserFinanceDataResponse | null;
}

// Helper to format currency from cents
const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Helper to format date range (last 90 days)
const formatDateRange = (): string => {
  const now = new Date();
  const ninetyDaysAgo = new Date(now);
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 89);
  const startStr = ninetyDaysAgo.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const endStr = now.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `${startStr} - ${endStr}`;
};

const formatPointerDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Generate chart data from NetWorthAnalysis using balance
// Normalizes values to 0-100 range to prevent extreme spikes from breaking the chart
// Also stores original value for pointer display
const generateChartData = (
  financeData: UserFinanceDataResponse | null
): { value: number; originalValue: number; date: string }[] | null => {
  if (!financeData?.analysis?.netWorth?.byDay?.length) {
    return null;
  }

  const { byDay } = financeData.analysis.netWorth;
  const values = byDay.map((d) => d.balance / 100);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal;

  // Normalize to 0-100 range, or use 50 if all values are the same
  // Store original value in cents for pointer display
  return byDay.map((day) => ({
    value: range === 0 ? 50 : ((day.balance / 100 - minVal) / range) * 100,
    originalValue: day.balance,
    date: day.date,
  }));
};

// Get net worth from the last day's balance
const getNetWorth = (
  financeData: UserFinanceDataResponse | null
): number | null => {
  if (!financeData?.analysis?.netWorth?.byDay?.length) {
    return null;
  }
  // Use the last day's balance as current net worth
  const { byDay } = financeData.analysis.netWorth;
  return byDay[byDay.length - 1].balance;
};

// Calculate trend percentage month-over-month
const calculateTrend = (
  financeData: UserFinanceDataResponse | null
): number | null => {
  const byDay = financeData?.analysis?.netWorth?.byDay;
  if (!byDay?.length) return null;

  // Aggregate daily data into monthly buckets (last day's balance per month)
  const monthlyMap: Record<string, number> = {};
  for (const day of byDay) {
    const monthKey = day.date.substring(0, 7); // "2025-01-15" -> "2025-01"
    monthlyMap[monthKey] = day.balance;
  }

  const months = Object.entries(monthlyMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, balance]) => balance);

  if (months.length < 2) return null;

  const currentMonth = months[months.length - 1];
  const previousMonth = months[months.length - 2];
  if (previousMonth === 0) return null;

  const pct = ((currentMonth - previousMonth) / Math.abs(previousMonth)) * 100;
  // Use 1 decimal place for small changes so we don't round to 0%
  if (Math.abs(pct) < 1 && Math.abs(pct) > 0) {
    return parseFloat(pct.toFixed(1));
  }
  return Math.round(pct);
};

export default memo(function NetWorthChart({
  financeData,
}: NetWorthChartProps) {
  const netWorth = getNetWorth(financeData);
  const trend = calculateTrend(financeData);
  const chartData = generateChartData(financeData);
  // Return null if no data
  if (netWorth === null || chartData === null) {
    return null;
  }

  const naturalSpacing =
    (CHART_WIDTH - 16 - POINTER_LABEL_WIDTH / 2) / (chartData.length - 1 || 1);
  const effectiveSpacing = Math.min(naturalSpacing, MAX_SPACING);
  const effectiveChartWidth =
    effectiveSpacing < naturalSpacing
      ? Math.round(
          8 + effectiveSpacing * (chartData.length - 1) + POINTER_LABEL_WIDTH / 2 + 8,
        )
      : CHART_WIDTH;

  // For net worth: positive state when net worth is positive AND trend is non-negative
  // If net worth is negative, always show as negative state regardless of trend
  const isPositiveNetWorth = netWorth >= 0;
  const isPositiveTrend = isPositiveNetWorth && (trend ?? 0) >= 0;

  const handleConnectMore = () => {
    router.push({
      pathname: "/my-money/setup",
      params: {
        type: SubscriptionSetupType.MyMoney,
      },
    });
  };

  return (
    <View style={styles.card}>
      {/* Header */}
      <Text style={styles.title}>Net worth</Text>

      {/* Amount with badge */}
      <View style={styles.amountRow}>
        <Text style={styles.amount}>{formatCurrency(netWorth)}</Text>
        {trend !== null && (
          <View
            style={[styles.badge, !isPositiveTrend && styles.badgeNegative]}
          >
            <Text
              style={[
                styles.badgeText,
                !isPositiveTrend && styles.badgeTextNegative,
              ]}
            >
              {isPositiveTrend ? "↑" : "↓"}
              {Math.abs(trend)}%
            </Text>
          </View>
        )}
      </View>

      {/* Date range */}
      <Text style={styles.dateRange}>{formatDateRange()}</Text>

      {/* Line Chart */}
      <View
        style={[
          styles.chartContainer,
          effectiveChartWidth < CHART_WIDTH && styles.chartContainerCentered,
        ]}
      >
        <LineChart
          data={chartData}
          parentWidth={effectiveChartWidth}
          width={effectiveChartWidth}
          height={150}
          // Extra canvas space so the pointer label isn't clipped at the top
          overflowTop={30}
          hideDataPoints
          hideRules
          hideYAxisText
          hideAxesAndRules
          thickness={4}
          curved
          curveType={1}
          color={Colors.primary}
          areaChart
          startFillColor={"rgba(15, 145, 41, 0.1)"}
          endFillColor={"rgba(15, 145, 41, 0)"}
          startOpacity={0.2}
          endOpacity={0}
          initialSpacing={8}
          endSpacing={POINTER_LABEL_WIDTH / 2 + 8}
          spacing={effectiveSpacing}
          isAnimated
          animationDuration={800}
          mostNegativeValue={-1}
          pointerConfig={{
            stripOverPointer: true,
            pointerStripWidth: 0,
            pointerStripColor: Colors.primary,
            pointerColor: Colors.primary,
            radius: 6,
            pointerComponent: () => (
              <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: Colors.primary, transform: [{ translateX: -7 }] }} />
            ),
            pointerLabelWidth: POINTER_LABEL_WIDTH,
            pointerLabelHeight: 46,
            activatePointersOnLongPress: false,
            autoAdjustPointerLabelPosition: true,
            persistPointer: true,
            pointerLabelComponent: (items: { originalValue: number; date: string }[]) => {
              const item = items[0];
              const dateLabel = item?.date ? formatPointerDate(item.date) : "";
              return (
                <View style={styles.pointerLabel}>
                  <Text style={styles.pointerDateText}>{dateLabel}</Text>
                  <Text style={styles.pointerLabelText}>
                    {formatCurrency(item?.originalValue ?? 0)}
                  </Text>
                </View>
              );
            },
          }}
        />
      </View>

      {/* Divider */}
      <View style={styles.divider} />

      {/* Connect more accounts */}
      <TouchableOpacity style={styles.connectRow} onPress={handleConnectMore}>
        <View>
          <Text style={styles.connectTitle}>Connect more accounts</Text>
          <Text style={styles.connectSubtitle}>See your full net worth</Text>
        </View>
        <Icons.ChevronRight width={24} height={24} />
      </TouchableOpacity>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    paddingVertical: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 2,
  },
  title: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    textAlign: "center",
    marginBottom: 8,
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginBottom: 4,
  },
  amount: {
    fontSize: 36,
    fontFamily: FontFamilies.InterTight.Bold,
    color: "#161D14",
  },
  badge: {
    backgroundColor: "#ECFDF5",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  badgeNegative: {
    backgroundColor: "#FEF2F2",
  },
  badgeText: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
  },
  badgeTextNegative: {
    color: "#EF4444",
  },
  dateRange: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
    textAlign: "center",
    marginBottom: 16,
  },
  chartContainer: {
    marginBottom: 16,
    paddingTop: 16,
  },
  chartContainerCentered: {
    alignItems: "center",
  },
  divider: {
    height: 1,
    backgroundColor: "#E5E7EB",
    marginBottom: 16,
  },
  connectRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  connectTitle: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    marginBottom: 2,
  },
  connectSubtitle: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#6B7280",
  },
  pointerLabel: {
    backgroundColor: "#161D14",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
  },
  pointerDateText: {
    fontSize: 11,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(255, 255, 255, 0.8)",
    textAlign: "center",
  },
  pointerLabelText: {
    fontSize: 14,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#FFFFFF",
    textAlign: "center",
  },
});
